import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { getFormattedCouponReduction } from "@modules/common/couponObject/utils/couponObjectUtils"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { HUText } from "@modules/ui/components/huText"
import dayjs from "dayjs"
import { Banknote, Percent, Tag } from "lucide-react"
import { useTheme } from "styled-components"

type ProgramSubscriberDetailsContentPriceProps = {
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscriberDetailsContentPrice({
  subscriberDetails,
  className,
  ...props
}: ProgramSubscriberDetailsContentPriceProps) {
  const defautlClassName = "flex flex-column gap-2 w-full"
  const theme = useTheme()
  const language = useLanguage()

  return (
    <div className={className ?? defautlClassName} {...props}>
      <div className="flex w-full gap-2 align-items-baseline">
        <HUText fontStyle="BS" color={theme.neutralColor700}>
          {word("program.subscribers.subscriptionDetails.price.label")}
        </HUText>
        <div className="flex flex-grow-1 border-top-1 border-neutralColor-300" />
      </div>
      {subscriberDetails.paidPrice && !subscriberDetails.trial ? (
        <div className="grid">
          <div className="col-6">
            {subscriberDetails.nextPayment && subscriberDetails.nextPayment.value >= 0 ? (
              <>
                <div className="flex flex-row gap-1 align-items-center py-1">
                  <Banknote color={theme.neutralColor600} size={16} />
                  <HUText fontStyle="LS" color={theme.neutralColor600}>
                    {word("program.subscribers.subscriptionDetails.nextPayment.label")}
                  </HUText>
                </div>
                <div className="flex flex-row gap-2 align-items-center py-1">
                  <HUText fontStyle="LM" color={theme.neutralColor900}>
                    {dayjs(subscriberDetails.nextPaymentDate).format("L")}
                  </HUText>
                  <div className="flex flex-row gap-1 align-items-baseline">
                    <HUText fontStyle="LL" color={theme.neutralColor900}>
                      {formatCurrency(
                        subscriberDetails.nextPayment.value,
                        subscriberDetails.nextPayment.currency,
                        language,
                      )}
                    </HUText>
                    {subscriberDetails.referencePrice &&
                    subscriberDetails.nextPayment &&
                    subscriberDetails.referencePrice.value !== subscriberDetails.nextPayment.value ? (
                      <HUText fontStyle="LM" color={theme.neutralColor500} className="line-through">
                        {formatCurrency(
                          subscriberDetails.referencePrice.value,
                          subscriberDetails.referencePrice.currency,
                          language,
                        )}
                      </HUText>
                    ) : null}
                  </div>
                </div>
              </>
            ) : (
              <HUText fontStyle="LL" color={theme.neutralColor900}>
                {subscriberDetails.getWordingPrice()}
              </HUText>
            )}
          </div>
          <div className="col-6">
            <div className="flex flex-row gap-1 align-items-center justify-content-end py-1">
              <HUText fontStyle="LS" fontWeight="semibold" color={theme.neutralColor600}>
                {word("program.subscribers.subscriptionDetails.lastPayment.label")}
              </HUText>
            </div>
            {subscriberDetails.lastInvoices && subscriberDetails.lastInvoices.length > 0 ? (
              subscriberDetails.lastInvoices.map((invoice, index) => (
                <div key={index} className="flex flex-row gap-1 align-items-center justify-content-end py-1">
                  <HUText fontStyle="LS" color={theme.neutralColor700}>
                    ({dayjs(invoice.invoiceDate).format("L")})
                  </HUText>
                  <HUText fontStyle="LS" color={theme.neutralColor700}>
                    {formatCurrency(invoice.price.value, invoice.price.currency, language)}
                  </HUText>
                </div>
              ))
            ) : (
              <HUText
                fontStyle="LS"
                color={theme.neutralColor700}
                className="flex flex-row gap-1 align-items-center justify-content-end py-1"
              >
                {word("program.subscribers.subscriptionDetails.noInvoice.label")}
              </HUText>
            )}
          </div>
        </div>
      ) : (
        <HUText fontStyle="LL" color={theme.neutralColor900}>
          {subscriberDetails.getWordingPrice()}
        </HUText>
      )}
      <div className="flex flex-column w-full gap-3">
        {/* PROMO CODE */}
        {subscriberDetails.usedPromoCode && subscriberDetails.paidPrice ? (
          <div className="flex flex-column w-full gap-2">
            <div className="flex gap-1 pl-3 align-items-center">
              <Percent size={15} color={theme.neutralColor600} />
              <HUText fontStyle="LS" color={theme.neutralColor600}>
                {word("box.member.table.offer.item.codePromo")}
              </HUText>
            </div>
            <div className="flex flex-rows align-items-baseline gap-1 pl-3">
              <HUText fontStyle="LM" color={theme.neutralColor900}>
                {subscriberDetails.usedPromoCode.code}
              </HUText>
              <HUText fontStyle="LS" color={theme.neutralColor700}>
                -
                {getFormattedCouponReduction(
                  subscriberDetails.usedPromoCode.type,
                  subscriberDetails.usedPromoCode.value,
                  language,
                  subscriberDetails.paidPrice.currency,
                )}
              </HUText>
            </div>
            <div className="flex flex-rows align-items-baseline gap-1 pl-3">
              <HUText fontStyle="LS" color={theme.neutralColor700}>
                {`(${subscriberDetails.usedPromoCode.getWordingDate()})`}
              </HUText>
            </div>
          </div>
        ) : null}
        {/* DISCOUNT */}
        {subscriberDetails.coupon && subscriberDetails.referencePrice ? (
          <div className="flex flex-column w-full gap-2">
            <div className="flex gap-1 pl-3 align-items-center">
              <Tag size={15} color={theme.neutralColor600} />
              <HUText fontStyle="LS" color={theme.neutralColor600} className="font-semibold">
                {word("box.member.table.offer.item.discount.label")}
              </HUText>
            </div>
            <div className="flex flex-column gap-1 pl-3">
              <HUText fontStyle="BS" color={theme.neutralColor900}>
                {getFormattedCouponReduction(
                  subscriberDetails.coupon.type,
                  subscriberDetails.coupon.value,
                  language,
                  subscriberDetails.referencePrice.currency,
                )}
                {subscriberDetails.coupon.type === CouponType.PERCENT
                  ? ` = -${subscriberDetails.getWordingCouponPriceAfterDiscount(language)}`
                  : null}
              </HUText>
              <HUText fontStyle="LS" color={theme.neutralColor700}>
                {subscriberDetails.getWordingCouponDate()}
              </HUText>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
